import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Trash2, 
  AlertTriangle, 
  ShieldCheck, 
  Loader2, 
  CheckCircle2,
  Lock
} from 'lucide-react';
import Section from '../components/Section';

const RemoveUser: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Lütfen geçerli bir e-posta adresi girin.');
      return;
    }

    try {
      setIsSubmitting(true);
      
      // API isteği arka planda çalışır, sayfa yönlendirmesi yapmaz.
      const response = await fetch('https://www.lotussport.net/exereyes/removeuser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('İşlem şu anda gerçekleştirilemiyor.');
      }

      setSuccess(true);
      setEmail('');
    } catch (err) {
      setError('Bir hata oluştu. Lütfen internet bağlantınızı kontrol edin veya daha sonra tekrar deneyin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {/* HERO / HEADER BÖLÜMÜ */}
      <section className="relative pt-32 pb-16 bg-primary-900 overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-10 pointer-events-none">
            <div className="absolute -top-20 -right-20 w-96 h-96 bg-red-500 rounded-full blur-[120px]"></div>
            <div className="absolute bottom-0 left-0 w-72 h-72 bg-primary-400 rounded-full blur-[100px]"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="w-16 h-16 bg-red-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 text-red-400 border border-red-500/30">
               <Lock size={32} />
            </div>
            <h1 className="text-3xl md:text-5xl font-display font-bold mb-4 text-white">
              Hesap ve Veri Yönetimi
            </h1>
            <p className="text-lg text-primary-100 max-w-2xl mx-auto">
              Gizliliğinize saygı duyuyoruz. Kişisel verileriniz üzerindeki kontrol tamamen sizdedir.
            </p>
          </motion.div>
        </div>
      </section>

      {/* MAIN CONTENT */}
      <Section className="py-16 -mt-10 relative z-20">
        <div className="bg-white rounded-3xl shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            
            {/* SOL TARAF: Bilgilendirme */}
            <div className="p-8 md:p-12 bg-gray-50 border-r border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                <ShieldCheck className="text-primary-600" />
                Veri Silme Politikası
              </h2>
              
              <div className="space-y-6 text-gray-600 leading-relaxed">
                <p>
                  Lotus Sport olarak, KVKK ve GDPR standartlarına tam uyum sağlamaktayız. Hesabınızı silme talebinde bulunduğunuzda aşağıdaki işlemler uygulanır:
                </p>
                
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="mt-1 min-w-[20px] h-5 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">1</div>
                    <span>Kişisel profil bilgileriniz ve egzersiz geçmişiniz veritabanımızdan kalıcı olarak silinir.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 min-w-[20px] h-5 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">2</div>
                    <span>Aktif abonelikleriniz (varsa) iptal edilir.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <div className="mt-1 min-w-[20px] h-5 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold">3</div>
                    <span>İşlem tamamlandığında size bir onay e-postası gönderilir.</span>
                  </li>
                </ul>

                <div className="mt-8 p-4 bg-yellow-50 border border-yellow-100 rounded-xl flex items-start gap-3">
                  <AlertTriangle className="text-yellow-600 shrink-0 mt-0.5" size={20} />
                  <p className="text-sm text-yellow-700">
                    <strong>Dikkat:</strong> Bu işlem geri alınamaz. Hesabınızı sildikten sonra verilerinize tekrar erişim sağlayamazsınız.
                  </p>
                </div>
              </div>
            </div>

            {/* SAĞ TARAF: Form */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <div className="mb-8">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Silme Talebi Oluştur</h3>
                <p className="text-gray-500 text-sm">
                  Exereyes hesabınıza bağlı e-posta adresinizi girerek süreci başlatın.
                </p>
              </div>

              {success ? (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center"
                >
                  <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 size={32} />
                  </div>
                  <h4 className="text-xl font-bold text-green-800 mb-2">Talep Alındı</h4>
                  <p className="text-green-700">
                    Hesap silme talebiniz başarıyla işleme alındı. Gerekli kontroller yapıldıktan sonra işleminiz tamamlanacaktır.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      E-posta Adresi
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="ornek@email.com"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
                      required
                    />
                  </div>

                  {error && (
                    <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-600 flex items-center gap-2">
                      <AlertTriangle size={16} />
                      {error}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center gap-2 bg-red-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-red-700 transition-all disabled:opacity-70 disabled:cursor-not-allowed shadow-lg shadow-red-600/20 hover:shadow-red-600/30"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="animate-spin" size={20} /> İşleniyor...
                      </>
                    ) : (
                      <>
                        <Trash2 size={20} /> Hesabımı Kalıcı Olarak Sil
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default RemoveUser;